import Settings from "./Settings";
import { AuthToken } from "./AuthToken";

import ProcessingResult from "../framework/ProcessingResult";

import TransformationMapper from "./mappers/TransformationMapper";
import DataTableReadMapper from "./mappers/DataTableReadMapper";
import GroupFilterMapper from "./mappers/GroupFilterMapper";

import DataTableReadDto from "../classes/dtos/pathcommonmodule/DataTableReadDto";

import GetPathSynchroDetailResult from "../classes/api/result/pathsynchromodule/GetPathSynchroDetailResult";
import UpdatePathSynchroDetailResult from "../classes/api/result/pathsynchromodule/UpdatePathSynchroDetailResult";
import CreateRequestResult from "../classes/api/result/CreateRequestResult";
import GetRequestPathSynchroResult from "../classes/api/result/pathsynchromodule/GetRequestPathSynchroResult";
import GetPathSynchroCapabilitiesResult from "../classes/api/result/pathsynchromodule/GetPathSynchroCapabilitiesResult";
import PathSynchroMappingDto from "../classes/dtos/pathsynchromodule/PathSynchroMappingDto";
import ScheduleDto from "../classes/dtos/schedulermodule/ScheduleDto";
import PathSynchroMappingCapabilitiesDto from "../classes/dtos/pathsynchromodule/PathSynchroMappingCapabilitiesDto";
import PathSynchroMappingCapabilityDto from "../classes/dtos/pathsynchromodule/PathSynchroMappingCapabilityDto";
import PathSynchroSetupDto from "../classes/dtos/pathsynchromodule/PathSynchroSetupDto";
import FieldReferenceIdAndKeyDto from "../classes/dtos/FieldReferenceIdAndKeyDto";
import GenerateAITransformationResult from "../classes/api/result/pathsynchromodule/GenerateAITransformationResult";
import FieldTypeReferenceDto from "../classes/dtos/FieldTypeReferenceDto";

const PathSynchroApi = {

    GetPathSynchroDetailAsync: async function (pathId: number): Promise<ProcessingResult<GetPathSynchroDetailResult>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/PathSynchro/GetPathSynchroDetail?pathId=" + pathId.toString(), {
            method: "Get",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            }
        });

        if (response.status === 200) {
            let data = await response.json();
            let source = DataTableReadMapper.ToDto(data.source);
            let target = DataTableReadMapper.ToDto(data.target);
            let mappings = data.mappings.map((e: any) => new PathSynchroMappingDto(e.isKey, e.sourceFieldReferenceId, e.targetFieldReferenceId, e.transformationLogicId, e.transformationParameters, GroupFilterMapper.RowFiltersToDtos(e.filters), GroupFilterMapper.DeclaredVariablesFromResponse(e.declaredVariables)));
            let schedule = new ScheduleDto(data.schedule.isActive, data.schedule.scheduleType, data.schedule.interval, data.schedule.time);
            let setup = new PathSynchroSetupDto(data.setup.additionActivated, data.setup.updateActivated, data.setup.deletionActivated);
            return ProcessingResult.Ok(new GetPathSynchroDetailResult(data.id, data.name, source, target, mappings, schedule, setup, data.lastVersionId, data.publishedVersionId, data.advancedFilterMode ?? false));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    UpdatePathSynchroDetailAsync: async function (pathId: number, name: string, mappings: PathSynchroMappingDto[], source: DataTableReadDto, target: DataTableReadDto, schedule: ScheduleDto, setup: PathSynchroSetupDto, advancedFilterMode: boolean, publish: boolean): Promise<ProcessingResult<UpdatePathSynchroDetailResult>> {
        let authSession = await AuthToken.get();

        let time = (schedule.Time / (60 * 1000)) - new Date().getTimezoneOffset();

        const response = await fetch(Settings.api_url + "/PathSynchro/UpdatePathSynchroDetail", {
            method: "Post",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            },
            body: JSON.stringify({
                'pathId': pathId,
                'name': name,
                'source': DataTableReadMapper.ToPayload(source),
                'target': DataTableReadMapper.ToPayload(target),
                // Unmapped rows carry null transformation (unlink clears it); the API expects non-nullable
                // int/string. CheckBeforeSaveAndPublish already blocks a *mapped* row with no transformation,
                // so coalescing to 0/"" here only affects unmapped (sourceFieldReferenceId 0) rows.
                'mappings': mappings.map((e) => ({ isKey: e.IsKey, sourceFieldReferenceId: e.SourceFieldReferenceId ?? 0, targetFieldReferenceId: e.TargetFieldReferenceId, transformationLogicId: e.TransformationLogicId ?? 0, transformationParameters: e.TransformationParameters ?? "", filters: GroupFilterMapper.RowFiltersToPayload(e.Filters), declaredVariables: GroupFilterMapper.DeclaredVariablesToPayload(e.DeclaredVariables) })),
                'schedule': {
                    'isActive': schedule.IsActive,
                    'scheduleType': schedule.Type,
                    'interval': schedule.Interval,
                    'time': time
                },
                'setup': {
                    additionActivated: setup.AdditionActivated,
                    updateActivated: setup.UpdateActivated,
                    deletionActivated: setup.DeletionActivated
                },
                'advancedFilterMode': advancedFilterMode,
                'Publish': publish
            })
        });

        if (response.status === 200) {
            let data = await response.json();
            return ProcessingResult.Ok(new UpdatePathSynchroDetailResult(data.id, data.isPublished));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    CreateRequestPathSynchroAsync: async function (pathId: number): Promise<ProcessingResult<CreateRequestResult>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/PathSynchro/CreateRequestPathSynchro", {
            method: "Post",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            },
            body: JSON.stringify({
                'pathId': pathId,
                'origin': 1
            })
        });

        if (response.status === 200) {
            let data = await response.json();
            return ProcessingResult.Ok(new CreateRequestResult(data.accepted, data.token));
        }

        // Surface the ProblemDetails `detail` so callers can tell an offline-agent error
        // ("Customer agent is not online ...") from a generic failure.
        let detail = "";
        try {
            let problem = await response.json();
            detail = problem?.detail ?? "";
        } catch {
            // Body missing or not JSON: fall back to the status code.
        }

        return ProcessingResult.Error(detail !== "" ? detail : "Error = " + response.status.toString());
    },

    GetRequestPathSynchroAsync: async function (taskId: string): Promise<ProcessingResult<GetRequestPathSynchroResult>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/PathSynchro/GetRequestPathSynchro?TaskId=" + taskId, {
            method: "Get",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            }
        });

        if (response.status === 200) {
            let data = await response.json();
            return ProcessingResult.Ok(new GetRequestPathSynchroResult(data.status, data.progress, data.errorCode));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    GetPathSynchroCapabilitiesAsync: async function (tableReferenceSourceId: number | null, pathSourceId: number | null, tableReferenceTargetId: number, withAutoMapping: boolean): Promise<ProcessingResult<GetPathSynchroCapabilitiesResult>> {
        let authSession = await AuthToken.get();

        let url = "/PathSynchro/GetPathSynchroCapabilities?TableReferenceTargetId=" + tableReferenceTargetId + "&WithAutoMapping=" + withAutoMapping;

        if (tableReferenceSourceId !== null) { url = url + "&TableReferenceSourceId=" + tableReferenceSourceId; }
        if (pathSourceId !== null) { url = url + "&PathSourceId=" + pathSourceId; }

        const response = await fetch(Settings.api_url + url, {
            method: "Get",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            }
        });

        if (response.status === 200) {
            let data = await response.json();
            let sourceFields = data.sourceFields.map((e: any) => new FieldReferenceIdAndKeyDto(e.id, e.sourceKey, e.datasetKey, e.name, new FieldTypeReferenceDto(e.type.type, e.type.isMultiple, e.type.typeLabel, e.type.isNullable, e.type.maxLength, e.type.isReadOnly, e.type.isCalculated, e.type.isBusinessRequired, e.type.isPrimaryKey, e.type.originTypeLabel, e.type.linkTableId)));
            let targetFields = data.targetFields.map((e: any) => new FieldReferenceIdAndKeyDto(e.id, e.sourceKey, e.datasetKey, e.name, new FieldTypeReferenceDto(e.type.type, e.type.isMultiple, e.type.typeLabel, e.type.isNullable, e.type.maxLength, e.type.isReadOnly, e.type.isCalculated, e.type.isBusinessRequired, e.type.isPrimaryKey, e.type.originTypeLabel, e.type.linkTableId)));
            let mappingsCapabilities = data.mappingsCapabilities.map((e: any) => new PathSynchroMappingCapabilitiesDto(e.targetFieldId, e.capabilities.map((x: any) => new PathSynchroMappingCapabilityDto(x.sourceFieldId, x.transformationLogicIds))));
            let transformationsCapabilities = data.transformationsCapabilities.map((e: any) => TransformationMapper.ToDto(e));
            return ProcessingResult.Ok(new GetPathSynchroCapabilitiesResult(sourceFields, targetFields, mappingsCapabilities, transformationsCapabilities, data.autoMappingResult));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    // Asks the AI to build a complex ("Combined") transformation from a natural-language prompt.
    // Resolves to the raw JSON settings (steps) to store as TransformationParameters, with the labels of the
    // transformations those steps use.
    // The path is needed by the API for the rights check and the generation log.
    GenerateAITransformationAsync: async function (pathId: number, tableReferenceSourceId: number | null, pathSourceId: number | null, tableReferenceTargetId: number, fieldReferenceTargetId: number, prompt: string): Promise<ProcessingResult<GenerateAITransformationResult>> {
        let authSession = await AuthToken.get();

        let url = "/PathSynchro/GenerateAITransformation?PathId=" + pathId + "&TableReferenceTargetId=" + tableReferenceTargetId +
            "&FieldReferenceTargetId=" + fieldReferenceTargetId + "&Prompt=" + encodeURIComponent(prompt);

        if (tableReferenceSourceId !== null) { url = url + "&TableReferenceSourceId=" + tableReferenceSourceId; }
        if (pathSourceId !== null) { url = url + "&PathSourceId=" + pathSourceId; }

        const response = await fetch(Settings.api_url + url, {
            method: "Get",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            }
        });

        if (response.status === 200) {
            let data = await response.json();
            return ProcessingResult.Ok(new GenerateAITransformationResult(
                data.result as string,
                data.labels ?? {},
                (data.inputColumns ?? []).map((c: any) => ({ FieldId: c.fieldId, Name: c.name })),
                data.outputColumnName ?? "",
                (data.rows ?? []).map((r: any) => ({ Inputs: r.inputs ?? {}, Output: r.output ?? null })),
                data.error ?? ""
            ));
        }

        // The API reports why the generation failed (e.g. the AI could not reach the target type) in the
        // ProblemDetails `detail`: surface it rather than a bare status code.
        let detail = "";
        try {
            let problem = await response.json();
            detail = problem?.detail ?? "";
        } catch {
            // Body missing or not JSON: fall back to the status code.
        }

        return ProcessingResult.Error(detail !== "" ? detail : "Error = " + response.status.toString());
    }
};

export default PathSynchroApi;